import {
  dbPortalGebiete,
  dbPortalKonzessionaere,
  dbPortalMeta,
  dbPortalSkala,
  dbPortalTabFassungen,
  type DbPortalGebiete,
  type DbPortalKonzessionaere,
  type DbPortalMeta,
  type DbPortalSkala,
  type DbPortalTabFassungen,
} from '@gql/shared-types';
import type { ZodType, ZodTypeDef } from 'zod';

/**
 * Datenbasis: Export aus aufschaltgebiete.db (apps/db-portal/daten/), ausgeliefert nur über
 * die API hinter Anmeldung und Vertraulichkeitszustimmung (/api/db-portal/daten/*).
 */
const BASIS = ((import.meta.env['VITE_DATEN_BASIS'] as string | undefined) ?? '/api/db-portal/daten').replace(/\/$/, '');

export interface Daten {
  gebiete: DbPortalGebiete;
  fassungen: DbPortalTabFassungen;
  konzessionaere: DbPortalKonzessionaere;
  skala: DbPortalSkala;
  meta: DbPortalMeta;
}

export class DatenFehler extends Error {}

async function holeJson<T>(datei: string, schema: ZodType<T, ZodTypeDef, unknown>): Promise<T> {
  let antwort: Response;
  try {
    antwort = await fetch(`${BASIS}/${datei}`, { credentials: 'same-origin' });
  } catch {
    throw new DatenFehler('Die Daten konnten nicht geladen werden. Bitte Verbindung prüfen und neu laden.');
  }
  if (!antwort.ok) throw new DatenFehler(`Datei ${datei} nicht verfügbar (HTTP ${antwort.status}).`);
  const ergebnis = schema.safeParse(await antwort.json());
  if (!ergebnis.success) {
    console.error(`Schemafehler in ${datei}`, ergebnis.error.issues.slice(0, 5));
    throw new DatenFehler(`Datei ${datei} entspricht nicht dem erwarteten Format.`);
  }
  return ergebnis.data;
}

let cache: Promise<Daten> | null = null;

export function ladeDaten(): Promise<Daten> {
  cache ??= Promise.all([
    holeJson('gebiete.geojson', dbPortalGebiete),
    holeJson('tab_fassungen.json', dbPortalTabFassungen),
    holeJson('konzessionaere.json', dbPortalKonzessionaere),
    holeJson('skala.json', dbPortalSkala),
    holeJson('meta.json', dbPortalMeta),
  ]).then(([gebiete, fassungen, konzessionaere, skala, meta]) => ({ gebiete, fassungen, konzessionaere, skala, meta }));
  cache.catch(() => {
    cache = null;
  });
  return cache;
}

/** Nach dem Abmelden: geladene Daten verwerfen. */
export function datenCacheLeeren(): void {
  cache = null;
}

export function laenderUrl(): string {
  return `${BASIS}/bundeslaender.geojson`;
}
